/* eslint-disable @typescript-eslint/no-explicit-any */
import { DiscountListItem } from '@/models/discount.model'
import { Paginated, PaginationParams } from '@/models/shared.model'
import { useFilterStore } from '@/store/filter'
import axios from 'axios'
import { format } from 'date-fns'
import { computed, Ref } from 'vue'
import { useMutation, useQuery } from 'vue-query'

export const discountKeys = {
  all: ['discount'] as const,
  lists: () => [...discountKeys.all, 'list'] as const,
  list: (
    companyId: Ref<number | null>,
    pagination: Ref<PaginationParams>,
    requestSwitch: Ref<boolean | null>,
  ) =>
    [
      ...discountKeys.lists(),
      { companyId, pagination, requestSwitch },
    ] as const,
  details: () => [...discountKeys.all, 'detail'] as const,
  detail: (companyId: Ref<number | null>, discountId: number) =>
    [...discountKeys.details(), { companyId, discountId }] as const,
}

export function useDiscountListQuery(
  companyId: Ref<number | null>,
  pagination: Ref<PaginationParams>,
  requestSwitch: Ref<boolean | null>,
  uuid?: Ref<string | null>,
) {
  return useQuery(
    discountKeys.list(companyId, pagination, requestSwitch),
    async () => {
      const { discountFilterObj } = useFilterStore()
      const { data } = await axios.get<Paginated<DiscountListItem[]>>(
        `companies/${companyId.value}/services/discount-codes/`,
        {
          params: {
            search: discountFilterObj.filters.search || undefined,
            service_id: discountFilterObj.filters.sale_articles
              ? parseInt(
                  (discountFilterObj.filters.sale_articles as { value: string })
                    .value,
                  10,
                )
              : undefined,
            status:
              typeof discountFilterObj.filters.validity === 'object' &&
              discountFilterObj.filters.validity !== null
                ? (discountFilterObj.filters.validity as { value: string })
                    .value
                : discountFilterObj.filters.validity || undefined,
            valid_start: discountFilterObj.filters.start_time
              ? format(
                  new Date(discountFilterObj.filters.start_time),
                  'yyyy-MM-dd',
                )
              : undefined,
            valid_end: discountFilterObj.filters.end_time
              ? format(
                  new Date(discountFilterObj.filters.end_time),
                  'yyyy-MM-dd',
                )
              : undefined,
            uuid: uuid?.value || undefined,
            page: pagination.value.page,
            page_size: pagination.value.page_size,
          },
        },
      )
      return data
    },
    {
      enabled: computed(() => !!companyId.value),
      retry: 0,
    },
  )
}

export async function createDiscountCode(companyId: number, discountData: any) {
  const { data } = await axios.post(
    `companies/${companyId}/services/discount-codes/`,
    discountData,
  )
  return data
}

export function useUpdateDiscountCodeMutation() {
  return useMutation(
    async ({
      companyId,
      discountUuid,
      payload,
    }: {
      companyId: number
      discountUuid: string
      payload: any
    }) => {
      const { data } = await axios.patch(
        `companies/${companyId}/services/discount-codes/?uuid=${discountUuid}`,
        payload,
      )
      return data
    },
  )
}

export function useDiscountDetailQuery(
  companyId: Ref<number | null>,
  discountUuid: number,
) {
  return useQuery(
    discountKeys.detail(companyId, discountUuid),
    async () => {
      const { data } = await axios.get<DiscountListItem>(
        `companies/${companyId.value}/services/discount-codes/?uuid=${discountUuid}`,
      )
      return data
    },
    {
      enabled: computed(() => !!companyId.value),
      retry: 0,
    },
  )
}

export async function deleteDiscountCode(
  companyId: number,
  discountUuid: string,
) {
  const { data } = await axios.delete(
    `companies/${companyId}/services/discount-codes/?uuid=${discountUuid}`,
  )
  return data
}
